import { useLayoutEffect } from 'react';

import { useNavigate } from 'react-router-dom';

import { useRefundEligibility } from 'hooks/refund-eligibility/use-refund-eligibility';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { routes } from 'utils/routing';

/**
 * This hook redirects the customer to the support flow if the order passed in is not eligible for a refund
 */
export const useRedirectToSupportFormIfRefundIneligible = (orderId: string) => {
  const {
    isDeterminingRefundEligibility,
    isOrderEligibleForRefund,
    refundIneligibilityReason
  } = useRefundEligibility({ orderId });
  const navigate = useNavigate();
  const enableGuestAndMultiStepSupportForm = useFlag(
    LaunchDarklyFlag.ENABLE_GUEST_AND_MULTI_STEP_SUPPORT_PAGE
  );

  /**
   * useLayoutEffect can prevent the component consuming this hook from rendering before redirecting,
   * eliminating a distracting flash of unintended content from showing
   */
  useLayoutEffect(() => {
    if (isDeterminingRefundEligibility) {
      return;
    }

    if (!isOrderEligibleForRefund) {
      // multistep support form allows including an orderId with submission
      // BUG: the url /support/order/:orderId no longer exists, needs to /support/order?orderId=___
      const url = enableGuestAndMultiStepSupportForm
        ? `${routes.support}/order/${orderId}`
        : routes.support;

      navigate(url, {
        state: { refundIneligibilityReason }
      });
    }
  }, [
    enableGuestAndMultiStepSupportForm,
    isDeterminingRefundEligibility,
    isOrderEligibleForRefund,
    navigate,
    orderId,
    refundIneligibilityReason
  ]);

  return { isDeterminingRefundEligibility };
};
